import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useTableColumnPrefs, type TableColumnDef } from '../hooks/useTableColumnPrefs'

import { previewCatalogMatch } from '../api/catalogImport'
import { matchDeterminationLabel, type MatchCandidateDto } from '../api/catalogImportTypes'
import { listProductTypes, mergeProductTypes, resolveProductTypes, type ProductTypeDto } from '../api/productTypes'
import {
  assignCatalogProductType,
  backfillCatalogBrands,
  bulkDeleteCatalogItems,
  confirmCatalogSuggestedMatch,
  createCatalogItem,
  deleteCatalogItem,
  dismissCatalogDuplicates,
  getCatalogSummary,
  listCatalogDuplicates,
  listSupplierCatalogItems,
  listSupplierCatalogFilterOptions,
  normalizeCatalogBrands,
  rematchSupplierCatalog,
  updateCatalogItemFields,
  type CatalogItemSortKey,
  type CatalogDuplicateGroupDto,
  type CatalogItemsPageDto,
  type CatalogSummaryDto,
  type SupplierCatalogItemDto,
} from '../api/supplierCatalog'
import {
  assignRestaurantMyPricesProductType,
  bulkDeleteRestaurantMyPricesItems,
  confirmRestaurantMyPricesSuggestion,
  getMyPricesCategorySuggestions,
  getMyPricesNameSuggestions,
  updateRestaurantMyPricesItemFields,
  deleteRestaurantMyPricesItem,
  dismissMyPricesDuplicates,
  getRestaurantMyPricesSummary,
  listMyPricesDuplicates,
  listRestaurantMyPricesFilterOptions,
  listRestaurantMyPricesItems,
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/restaurantMyPrices'
import {
  addCatalogListItems,
  addCatalogListItem,
  createCatalogList,
  deleteCatalogList,
  listCatalogLists,
  removeCatalogListItem,
  renameCatalogList,
  type CatalogListDto,
} from '../api/catalogLists'
import { ProductTypeCascadePicker } from './ProductTypeCascadePicker'
import { CatalogItemEditPanel, type CatalogItemEditMode } from './CatalogItemEditPanel'
import { ConfirmDialog } from './ConfirmDialog'
import { AttentionChip } from './myPrices/AttentionChip'
import { getMyPricesEmptyState } from './myPrices/emptyState'
import { CellEditor, type CellEditorGroup } from './myPrices/CellEditor'
import { ItemCardModal } from './myPrices/ItemCardModal'
import { NEEDS_ATTENTION_FILTER, needsAttention } from './myPrices/attention'
import {
  categoryGroups,
  neighbourColumn,
  parsePrice,
  supplierGroups,
  unitGroups,
  type CellEditTarget,
  type MyPricesEditableColumn,
} from './myPrices/inlineEdit'
import { CatalogDuplicateMergePanel } from './CatalogDuplicateMergePanel'
import { CatalogVersionHistoryPanel } from './CatalogVersionHistoryPanel'
import { IconCheck, IconListPlus, IconPencil, IconStar, IconTrash } from './icons/CatalogActionIcons'
import { CatalogListDialog } from './CatalogListDialog'
import { CatalogBulkListDialog } from './CatalogBulkListDialog'
import { CatalogListsManagerDialog } from './CatalogListsManagerDialog'
import { SuggestTextField } from './SuggestTextField'
import { MarketEmptyState } from './MarketEmptyState'
import { MarketPageHeader } from './MarketPageHeader'
import { PageScrollJumpButtons } from './PageScrollJumpButtons'
import { DataTable } from './DataTable'
import { ResizableTh } from './ResizableTh'
import { SelectCombobox } from './SelectCombobox'
import { TableColumnSettings } from './TableColumnSettings'
import { TableDensityToggle } from './TableDensityToggle'
import { useTableDensity } from '../hooks/useTableDensity'
import { useSort } from '../hooks/useSort'
import { formatProductTypeLabel, expandProductTypeToDescendantIds } from '../screens/catalogFilters'
import { formatAnalyticsPct, priceMoveToneClass } from '../screens/marketAnalyticsFormat'
import { formatUnitLabel } from '../utils/formatUnitLabel'
import { cartLineKey } from '../cart/cartLineKey'
import { useCart } from '../cart/CartProvider'
import { useToast } from './ToastProvider'
import { ogrnLabel } from '../utils/ogrn'

const formatCatalogPrice = (item: SupplierCatalogItemDto): string => {
  if (item.price != null) {
    return String(item.price)
  }
  const unit = item.unit?.trim()
  if (unit && /^\d+([.,]\d+)?$/.test(unit)) {
    return unit
  }
  return '—'
}

const formatCatalogPriceRub = (price: number | null): string => {
  if (price == null || Number.isNaN(price)) {
    return '—'
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(price)
}

const formatUpdatedAt = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const freshnessHint = (
  item: SupplierCatalogItemDto,
  /** Supplier admin sees delist status; restaurant never does — hidden rows simply stay out of search. */
  showDelistedHint: boolean,
): string | null => {
  if (showDelistedHint && item.buyerSearchVisible === false) {
    return 'Скрыта из выдачи покупателям'
  }
  if (item.freshnessStatus == null || item.freshnessStatus === 'fresh') return null
  if (item.daysSinceUpdate == null) return null
  return `${item.daysSinceUpdate} дн. без обновления`
}

const duplicateGroupWord = (count: number): string => {
  const lastTwo = count % 100
  const last = count % 10
  if (lastTwo >= 11 && lastTwo <= 14) return 'групп'
  if (last === 1) return 'группа'
  if (last >= 2 && last <= 4) return 'группы'
  return 'групп'
}

const duplicateGroupNeedsWord = (count: number): string =>
  count % 10 === 1 && count % 100 !== 11 ? 'требует' : 'требуют'

type DuplicateNoticeProps = {
  groupCount: number
  onOpen: () => void
}

/** Не вытесняет рабочую таблицу: детальный разбор открывается только по явному действию. */
const DuplicateNotice = ({ groupCount, onOpen }: DuplicateNoticeProps) => (
  <section className="market-duplicates-notice" aria-label="Найдены вероятные дубликаты">
    <div className="market-duplicates-notice__body">
      <span className="market-duplicates-notice__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M8 7.5h9.5A2.5 2.5 0 0 1 20 10v7.5a2.5 2.5 0 0 1-2.5 2.5H10A2.5 2.5 0 0 1 7.5 17.5V8" />
          <path d="M4 15.5V6.5A2.5 2.5 0 0 1 6.5 4H15" />
        </svg>
      </span>
      <div>
        <strong className="market-duplicates-notice__title">Найдены вероятные дубликаты</strong>
        <span className="market-duplicates-notice__text">
          {groupCount} {duplicateGroupWord(groupCount)} {duplicateGroupNeedsWord(groupCount)} проверки перед объединением.
        </span>
      </div>
    </div>
    <button type="button" className="echat-btn echat-btn--ghost echat-btn--inline" onClick={onOpen}>
      Посмотреть список
    </button>
  </section>
)

const SearchIcon = () => (
  <svg className="market-filter-search-control__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <circle cx="10.75" cy="10.75" r="5.75" />
    <path d="m15.2 15.2 4.3 4.3" strokeLinecap="round" />
  </svg>
)

const FiltersIcon = () => (
  <svg className="market-filters__more-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path d="M4 7h10M18 7h2M10 12h10M4 12h2M4 17h6M14 17h6" strokeLinecap="round" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="8" cy="12" r="2" />
    <circle cx="12" cy="17" r="2" />
  </svg>
)

const SUPPLIER_CATALOG_COLUMNS: TableColumnDef[] = [
  {
    id: 'select',
    label: 'Выбор',
    required: true,
    pin: 'start',
    hideInSettings: true,
    defaultWidth: 48,
    minWidth: 40,
  },
  { id: 'name', label: 'Позиция', required: true, defaultWidth: 240, minWidth: 120 },
  { id: 'sku', label: 'Артикул', defaultVisible: false, defaultWidth: 120, minWidth: 72 },
  { id: 'price', label: 'Цена', defaultWidth: 200, minWidth: 140 },
  { id: 'unit', label: 'Ед. изм.', defaultWidth: 88, minWidth: 64 },
  { id: 'package', label: 'Фасовка', defaultVisible: false, defaultWidth: 110, minWidth: 72 },
  {
    id: 'minShipment',
    label: 'Мин. отгрузка',
    defaultVisible: false,
    defaultWidth: 110,
    minWidth: 72,
  },
  { id: 'brand', label: 'Бренд', defaultWidth: 120, minWidth: 72 },
  { id: 'country', label: 'Страна', defaultWidth: 110, minWidth: 72 },
  { id: 'category', label: 'Категория', defaultWidth: 160, minWidth: 96 },
  { id: 'updated', label: 'Дата обновления', defaultWidth: 140, minWidth: 96 },
  { id: 'match', label: 'Тип определения', defaultWidth: 140, minWidth: 96 },
  {
    id: 'actions',
    label: 'Действия',
    required: true,
    pin: 'end',
    hideInSettings: true,
    defaultWidth: 120,
    minWidth: 96,
  },
]

const MY_PRICES_SUPPLIER_COLUMN: TableColumnDef = {
  id: 'supplier',
  label: 'Поставщик',
  defaultWidth: 180,
  minWidth: 96,
}

const ADD_CATALOG_LIST_OPTION = '__add_catalog_list__'

export type SupplierCatalogPageProps = {
  organizationId: number
  importPath: string
  catalogMode?: CatalogItemEditMode
  title?: string
  description?: string
  backLink?: ReactNode
  /** Operator-only: full catalog version history + restore. */
  showCatalogVersions?: boolean
}

type DeleteConfirmation =
  | { kind: 'item'; item: SupplierCatalogItemDto }
  | { kind: 'selection'; itemIds: number[] }
  | null

const isCatalogItemSortKey = (columnId: string): columnId is CatalogItemSortKey =>
  [
    'name',
    'sku',
    'price',
    'unit',
    'package',
    'minShipment',
    'brand',
    'country',
    'category',
    'updated',
    'match',
    'supplier',
  ].includes(columnId)

export const SupplierCatalogPage = ({
  organizationId,
  importPath,
  catalogMode = 'supplier',
  title = 'Каталог',
  description,
  backLink,
  showCatalogVersions = false,
}: SupplierCatalogPageProps) => {
  const isRestaurantMyPrices = catalogMode === 'restaurant-my-prices'
  const headerDescription =
    description !== undefined
      ? description
      : isRestaurantMyPrices
        ? undefined
        : 'Позиции прайс-листа и сопоставление с категориями Провизиум.'
  /* Bump suffix when default columns change so stale localStorage prefs drop. */
  const tableId = isRestaurantMyPrices ? 'buyer:my-prices:v7' : 'supplier:catalog:v3'
  const catalogColumns = useMemo(() => {
    if (!isRestaurantMyPrices) {
      return SUPPLIER_CATALOG_COLUMNS
    }
    // «Тип определения» — внутренняя кухня матчера; пользователю «Моих прайсов» достаточно
    // точки «требует внимания» у категории.
    const hiddenByDefault = new Set(['brand', 'country'])
    const base = SUPPLIER_CATALOG_COLUMNS.filter((column) => column.id !== 'match').map((column) => {
      if (column.id === 'actions') {
        // Star, lists, edit and delete must stay in one row without truncating the last action.
        return { ...column, defaultWidth: 184, minWidth: 152 }
      }
      return hiddenByDefault.has(column.id) ? { ...column, defaultVisible: false } : column
    })
    const nameIndex = base.findIndex((column) => column.id === 'name')
    if (nameIndex < 0) {
      return [...base, MY_PRICES_SUPPLIER_COLUMN]
    }
    return [...base.slice(0, nameIndex + 1), MY_PRICES_SUPPLIER_COLUMN, ...base.slice(nameIndex + 1)]
  }, [isRestaurantMyPrices])
  const columnPrefs = useTableColumnPrefs(tableId, catalogColumns)
  const { density, setDensity, tableClass } = useTableDensity()
  const { sort: tableSort, toggleSort, getDirection } = useSort<CatalogItemSortKey>()
  const navigate = useNavigate()
  const { addOffer, hasLine } = useCart()
  const { showToast } = useToast()
  const [summary, setSummary] = useState<CatalogSummaryDto | null>(null)
  const [page, setPage] = useState<CatalogItemsPageDto | null>(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([])
  const [brandOptions, setBrandOptions] = useState<string[]>([])
  const [countryOptions, setCountryOptions] = useState<string[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [matchStatus, setMatchStatus] = useState(() =>
    isRestaurantMyPrices && searchParams.get('attention') === '1' ? NEEDS_ATTENTION_FILTER : '',
  )
  const attentionOnly = isRestaurantMyPrices && matchStatus === NEEDS_ATTENTION_FILTER
  const [editing, setEditing] = useState<CellEditTarget | null>(null)
  const [editAnchor, setEditAnchor] = useState<HTMLTableCellElement | null>(null)
  const [categoryAlternative, setCategoryAlternative] = useState<{
    itemId: number
    candidate: MatchCandidateDto | null
  } | null>(null)
  const [cardItem, setCardItem] = useState<SupplierCatalogItemDto | null>(null)
  const [acceptingCategories, setAcceptingCategories] = useState(false)
  const [productTypeFilter, setProductTypeFilter] = useState<number | null>(null)
  const [unassignedOnly, setUnassignedOnly] = useState(false)
  const [privateSupplierId, setPrivateSupplierId] = useState<number | null>(null)
  const [privateSuppliers, setPrivateSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [catalogLists, setCatalogLists] = useState<CatalogListDto[]>([])
  const [catalogListId, setCatalogListId] = useState<number | null>(null)
  const [listDialogItem, setListDialogItem] = useState<SupplierCatalogItemDto | null>(null)
  const [listManagerOpen, setListManagerOpen] = useState(false)
  const [bulkListDialogOpen, setBulkListDialogOpen] = useState(false)
  const [listUpdatingKey, setListUpdatingKey] = useState<string | null>(null)
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newItemName, setNewItemName] = useState('')
  const [newItemBrand, setNewItemBrand] = useState('')
  const [newItemSku, setNewItemSku] = useState('')
  const [newItemCountry, setNewItemCountry] = useState('')
  const [newItemPrice, setNewItemPrice] = useState('')
  const [newItemUnit, setNewItemUnit] = useState('')
  const [newItemMinShipmentQty, setNewItemMinShipmentQty] = useState('')
  const [newItemPackageSize, setNewItemPackageSize] = useState('')
  const [newItemProductTypeId, setNewItemProductTypeId] = useState<number | null>(null)
  const [createCandidates, setCreateCandidates] = useState<MatchCandidateDto[]>([])
  const [loadingCreateSuggestions, setLoadingCreateSuggestions] = useState(false)
  const [creatingItem, setCreatingItem] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [rematching, setRematching] = useState(false)
  const [tidyingBrands, setTidyingBrands] = useState(false)
  const [rematchMessage, setRematchMessage] = useState<string | null>(null)
  const [duplicateGroups, setDuplicateGroups] = useState<CatalogDuplicateGroupDto[]>([])
  const [showDuplicates, setShowDuplicates] = useState(false)
  const [confirmingId, setConfirmingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>(null)
  const [error, setError] = useState<string | null>(null)
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  // Держим свежий productTypes в ref, чтобы async-загрузка читала его без добавления
  // в зависимости колбэка. Синхронизируем в эффекте (после рендера), а не во время рендера.
  const productTypesRef = useRef(productTypes)
  useEffect(() => {
    productTypesRef.current = productTypes
  }, [productTypes])

  const typeNames = new Map(productTypes.map((type) => [type.id, type.name]))
  const favoriteList = catalogLists.find((list) => list.systemKey === 'favorites') ?? null

  const load = useCallback(
    async (
      index: number,
      statusFilter: string,
      typeFilter: number | null,
      onlyUnassigned: boolean,
      searchQuery: string,
      supplierFilterId: number | null,
    ) => {
      if (!Number.isFinite(organizationId) || organizationId <= 0) {
        return
      }
      setLoading(true)
      setError(null)
      try {
        const [summaryData, pageData] = await Promise.all([
          isRestaurantMyPrices
            ? getRestaurantMyPricesSummary(organizationId)
            : getCatalogSummary(organizationId),
          isRestaurantMyPrices
            ? listRestaurantMyPricesItems({
                organizationId,
                page: index,
                matchStatus:
                  statusFilter && statusFilter !== NEEDS_ATTENTION_FILTER ? statusFilter : undefined,
                needsAttention: statusFilter === NEEDS_ATTENTION_FILTER || undefined,
                productTypeId: typeFilter ?? undefined,
                productTypeIds:
                  typeFilter != null
                    ? expandProductTypeToDescendantIds(typeFilter, productTypesRef.current)
                    : undefined,
                unassignedProductType: onlyUnassigned || undefined,
                search: searchQuery || undefined,
                privateSupplierId: supplierFilterId,
                catalogListId,
                sortBy: tableSort?.key,
                sortDirection: tableSort?.direction,
              })
            : listSupplierCatalogItems({
                organizationId,
                page: index,
                matchStatus: statusFilter || undefined,
                productTypeId: typeFilter ?? undefined,
                unassignedProductType: onlyUnassigned || undefined,
                search: searchQuery || undefined,
                sortBy: tableSort?.key,
                sortDirection: tableSort?.direction,
              }),
        ])
        setSummary(summaryData)
        setPage(pageData)
        setPageIndex(index)
        setSelectedIds([])
        try {
          const duplicates = isRestaurantMyPrices
            ? await listMyPricesDuplicates(organizationId)
            : await listCatalogDuplicates(organizationId)
          // Бэкенд уже отфильтровал группы, разведённые решением организации.
          setDuplicateGroups(duplicates.groups)
          setShowDuplicates(false)
        } catch {
          setDuplicateGroups([])
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить каталог')
      } finally {
        setLoading(false)
      }
    },
    [organizationId, isRestaurantMyPrices, catalogListId, tableSort],
  )

  useEffect(() => {
    void listProductTypes().then(setProductTypes).catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!page?.items.length) {
      return
    }
    const knownIds = new Set(productTypes.map((type) => type.id))
    const missingIds = [
      ...new Set(
        page.items
          .map((item) => item.productTypeId)
          .filter((id): id is number => id != null && !knownIds.has(id)),
      ),
    ]
    if (missingIds.length === 0) {
      return
    }
    void resolveProductTypes(missingIds)
      .then((resolved) => {
        setProductTypes((current) => mergeProductTypes(current, resolved))
      })
      .catch(() => undefined)
  }, [page, productTypes])

  const loadFilterOptions = useCallback(() => {
    if (!Number.isFinite(organizationId) || organizationId <= 0) {
      return
    }
    void (isRestaurantMyPrices
      ? listRestaurantMyPricesFilterOptions(organizationId)
      : listSupplierCatalogFilterOptions(organizationId))
      .then((options) => {
        setBrandOptions(options.brands)
        setCountryOptions(options.countries)
      })
      .catch(() => undefined)
  }, [organizationId, isRestaurantMyPrices])

  useEffect(() => {
    loadFilterOptions()
  }, [loadFilterOptions])

  useEffect(() => {
    if (!isRestaurantMyPrices || !Number.isFinite(organizationId) || organizationId <= 0) {
      setPrivateSuppliers([])
      setCatalogLists([])
      return
    }
    void listRestaurantPrivateSuppliers(organizationId)
      .then(setPrivateSuppliers)
      .catch(() => setPrivateSuppliers([]))
    void listCatalogLists(organizationId)
      .then(setCatalogLists)
      .catch(() => setCatalogLists([]))
  }, [organizationId, isRestaurantMyPrices])

  useEffect(() => {
    void load(0, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
  }, [load, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId])

  useEffect(() => {
    const name = newItemName.trim()
    if (!showCreateForm || !name) {
      setCreateCandidates([])
      return
    }
    setLoadingCreateSuggestions(true)
    const timer = window.setTimeout(() => {
      void previewCatalogMatch(organizationId, name, newItemBrand.trim() || null)
        .then((preview) => setCreateCandidates(preview.candidates))
        .catch(() => setCreateCandidates([]))
        .finally(() => setLoadingCreateSuggestions(false))
    }, 300)
    return () => window.clearTimeout(timer)
  }, [organizationId, newItemBrand, newItemName, showCreateForm])

  const resetCreateForm = () => {
    setNewItemName('')
    setNewItemBrand('')
    setNewItemSku('')
    setNewItemCountry('')
    setNewItemPrice('')
    setNewItemUnit('')
    setNewItemMinShipmentQty('')
    setNewItemPackageSize('')
    setNewItemProductTypeId(null)
    setCreateCandidates([])
  }

  const handleSearch = () => {
    setSearch(searchDraft.trim())
  }

  const handleResetSearch = () => {
    setSearchDraft('')
    setSearch('')
    setMatchStatus('')
    setProductTypeFilter(null)
    setUnassignedOnly(false)
    setPrivateSupplierId(null)
    setCatalogListId(null)
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        params.delete('attention')
        return params
      },
      { replace: true },
    )
  }

  const toggleSelected = (itemId: number) => {
    setSelectedIds((current) =>
      current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId],
    )
  }

  const cartKeyForItem = (item: SupplierCatalogItemDto) =>
    cartLineKey({
      supplierKind: 'external',
      supplierOrganizationId: null,
      privateSupplierId: item.privateSupplierId,
      catalogItemId: item.id,
    })

  const handleBulkAddToCart = () => {
    if (!isRestaurantMyPrices || !page) {
      return
    }
    const selected = page.items.filter((item) => selectedIds.includes(item.id))
    let added = 0
    let skippedNoType = 0
    let alreadyInCart = 0
    for (const item of selected) {
      if (item.productTypeId == null) {
        skippedNoType += 1
        continue
      }
      if (hasLine(cartKeyForItem(item))) {
        alreadyInCart += 1
        continue
      }
      addOffer({
        offer: {
          itemId: item.id,
          supplierOrganizationId: null,
          supplierOrganizationName: item.privateSupplierName ?? 'Поставщик',
          privateSupplierId: item.privateSupplierId,
          legalName: null,
          ogrn: null,
          inn: null,
          isRegisteredSupplier: false,
          itemName: item.supplierName,
          brand: item.brand,
          country: item.country,
          unit: item.unit,
          price: item.price != null ? String(item.price) : null,
          minShipmentQty: item.minShipmentQty != null ? String(item.minShipmentQty) : null,
          packageSize: item.packageSize,
          productTypeId: item.productTypeId,
          productTypeCode: item.productTypeCode,
          productTypeName: item.productTypeName,
          productTypeActive: item.productTypeActive,
          supplierResponseScore: null,
          rankingPrice: null,
          matchedTags: [],
        },
        productTypeName: formatProductTypeLabel(
          item.productTypeId,
          item.productTypeName,
          item.productTypeActive,
          new Map(),
        ),
      })
      added += 1
    }
    if (added === 0 && skippedNoType === 0) {
      showToast(alreadyInCart > 0 ? 'Выбранные позиции уже в заявке' : 'Нечего добавлять')
      return
    }
    showToast(
      skippedNoType > 0
        ? `Добавлено: ${added}. Без категории пропущено: ${skippedNoType}`
        : `Добавлено в заявку: ${added}`,
    )
  }

  const handleBulkEdit = () => {
    if (selectedIds.length !== 1) {
      showToast('Выберите одну позицию для редактирования')
      return
    }
    if (isRestaurantMyPrices) {
      setCardItem(page?.items.find((item) => item.id === selectedIds[0]) ?? null)
      return
    }
    setExpandedId(selectedIds[0] ?? null)
  }

  const handleItemSaved = (updated: SupplierCatalogItemDto) => {
    setPage((current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) => (item.id === updated.id ? updated : item)),
          }
        : current,
    )
    void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
  }

  const updateItemListMembership = (itemId: number, listId: number, selected: boolean) => {
    const update = (item: SupplierCatalogItemDto): SupplierCatalogItemDto => {
      if (item.id !== itemId) return item
      const current = item.catalogListIds ?? []
      const catalogListIds = selected
        ? [...new Set([...current, listId])]
        : current.filter((id) => id !== listId)
      return { ...item, catalogListIds }
    }
    setPage((current) =>
      current ? { ...current, items: current.items.map(update) } : current,
    )
    setListDialogItem((current) => (current ? update(current) : current))
    setCardItem((current) => (current ? update(current) : current))
  }

  const handleListToggle = async (item: SupplierCatalogItemDto, listId: number, selected: boolean) => {
    const key = `${item.id}:${listId}`
    setListUpdatingKey(key)
    setError(null)
    try {
      if (selected) {
        await addCatalogListItem(organizationId, listId, item.id)
      } else {
        await removeCatalogListItem(organizationId, listId, item.id)
      }
      updateItemListMembership(item.id, listId, selected)
      const list = catalogLists.find((candidate) => candidate.id === listId)
      showToast(selected ? `Добавлено в «${list?.name ?? 'список'}»` : `Убрано из «${list?.name ?? 'списка'}»`)
    } catch (listError) {
      setError(listError instanceof Error ? listError.message : 'Не удалось изменить список')
    } finally {
      setListUpdatingKey(null)
    }
  }

  const addList = async (name: string) => {
    const created = await createCatalogList(organizationId, name)
    setCatalogLists((current) =>
      [...current, created].sort(
        (left, right) =>
          Number(left.systemKey == null) - Number(right.systemKey == null) ||
          left.name.localeCompare(right.name, 'ru'),
      ),
    )
    return created
  }

  const handleCreateList = async (name: string) => {
    const created = await addList(name)
    if (listDialogItem) {
      await handleListToggle(listDialogItem, created.id, true)
    }
  }

  const handleCreateManagedList = async (name: string) => {
    const created = await addList(name)
    showToast('Список «' + created.name + '» создан')
  }

  const handleRenameList = async (list: CatalogListDto, name: string) => {
    const renamed = await renameCatalogList(organizationId, list.id, name)
    setCatalogLists((current) =>
      [...current.map((candidate) => (candidate.id === list.id ? renamed : candidate))].sort(
        (left, right) =>
          Number(left.systemKey == null) - Number(right.systemKey == null) ||
          left.name.localeCompare(right.name, 'ru'),
      ),
    )
    showToast(`Список переименован в «${renamed.name}»`)
  }

  const handleBulkAddToList = async (listId: number) => {
    const itemIds = [...new Set(selectedIds)]
    if (itemIds.length === 0) return
    setListUpdatingKey(`bulk:${listId}`)
    setError(null)
    try {
      const addedCount = await addCatalogListItems(organizationId, listId, itemIds)
      const addMembership = (item: SupplierCatalogItemDto): SupplierCatalogItemDto =>
        itemIds.includes(item.id)
          ? { ...item, catalogListIds: [...new Set([...(item.catalogListIds ?? []), listId])] }
          : item
      setPage((current) =>
        current ? { ...current, items: current.items.map(addMembership) } : current,
      )
      setListDialogItem((current) => (current ? addMembership(current) : current))
      setCardItem((current) => (current ? addMembership(current) : current))
      const list = catalogLists.find((candidate) => candidate.id === listId)
      const alreadyAdded = itemIds.length - addedCount
      showToast(
        addedCount === 0
          ? `Все выбранные позиции уже есть в «${list?.name ?? 'списке'}»`
          : alreadyAdded > 0
            ? `Добавлено в «${list?.name ?? 'список'}»: ${addedCount}. Уже были в списке: ${alreadyAdded}`
            : `Добавлено в «${list?.name ?? 'список'}»: ${addedCount}`,
      )
      setSelectedIds([])
    } catch (addError) {
      setError(addError instanceof Error ? addError.message : 'Не удалось добавить позиции в список')
      throw addError
    } finally {
      setListUpdatingKey(null)
    }
  }

  const handleOpenList = (list: CatalogListDto) => {
    setCatalogListId(list.id)
    setListManagerOpen(false)
  }

  const handleDeleteList = async (list: CatalogListDto) => {
    await deleteCatalogList(organizationId, list.id)
    setCatalogLists((current) => current.filter((candidate) => candidate.id !== list.id))
    if (catalogListId === list.id) {
      setCatalogListId(null)
    }
    const withoutDeletedList = (item: SupplierCatalogItemDto): SupplierCatalogItemDto => ({
      ...item,
      catalogListIds: (item.catalogListIds ?? []).filter((id) => id !== list.id),
    })
    setPage((current) =>
      current ? { ...current, items: current.items.map(withoutDeletedList) } : current,
    )
    setListDialogItem((current) => (current ? withoutDeletedList(current) : current))
    setCardItem((current) => (current ? withoutDeletedList(current) : current))
    showToast('Список «' + list.name + '» удалён')
  }

  const handleItemDeleted = () => {
    setExpandedId(null)
    void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
  }

  const handleConfirmMatch = async (item: SupplierCatalogItemDto) => {
    const status = item.matchStatus.toLowerCase()
    if (status !== 'suggested' && status !== 'auto') {
      return
    }
    if (item.productTypeId == null) {
      return
    }
    setConfirmingId(item.id)
    setError(null)
    try {
      const updated = isRestaurantMyPrices
        ? await confirmRestaurantMyPricesSuggestion(item.id)
        : await confirmCatalogSuggestedMatch(item.id)
      handleItemSaved(updated)
      const nextSummary = isRestaurantMyPrices
        ? await getRestaurantMyPricesSummary(organizationId)
        : await getCatalogSummary(organizationId)
      setSummary(nextSummary)
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : 'Не удалось подтвердить категорию')
    } finally {
      setConfirmingId(null)
    }
  }

  const toggleAttention = () => {
    const next = !attentionOnly
    setMatchStatus(next ? NEEDS_ATTENTION_FILTER : '')
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        if (next) params.set('attention', '1')
        else params.delete('attention')
        return params
      },
      { replace: true },
    )
  }

  const acceptableSelected = isRestaurantMyPrices
    ? (page?.items ?? []).filter(
        (item) => selectedIds.includes(item.id) && needsAttention(item) && item.productTypeId != null,
      )
    : []

  const handleAcceptCategories = async () => {
    setAcceptingCategories(true)
    setError(null)
    let accepted = 0
    try {
      for (const item of acceptableSelected) {
        await confirmRestaurantMyPricesSuggestion(item.id)
        accepted++
      }
      showToast(`Категории подтверждены: ${accepted}`)
    } catch (acceptError) {
      setError(acceptError instanceof Error ? acceptError.message : 'Не удалось подтвердить категории')
    } finally {
      setAcceptingCategories(false)
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    }
  }

  const isEditing = (item: SupplierCatalogItemDto, column: MyPricesEditableColumn) =>
    isRestaurantMyPrices && editing?.itemId === item.id && editing.column === column

  /** Клик по ячейке «Моих прайсов» — быстрое редактирование; выделение строки остаётся за чекбоксом. */
  const editableCellProps = (item: SupplierCatalogItemDto, column: MyPricesEditableColumn, className?: string) => {
    if (!isRestaurantMyPrices) {
      return { className }
    }
    const active = isEditing(item, column)
    return {
      className: ['market-editable-cell', active ? 'market-editable-cell--active' : null, className]
        .filter(Boolean)
        .join(' '),
      title: active ? undefined : 'Нажмите, чтобы изменить',
      ref: active ? setEditAnchor : undefined,
      onClick: (event: { stopPropagation: () => void }) => {
        event.stopPropagation()
        if (!active) startEdit(item, column)
      },
    }
  }

  const startEdit = (item: SupplierCatalogItemDto, column: MyPricesEditableColumn) => {
    setEditing({ itemId: item.id, column })
    if (column === 'category' && needsAttention(item) && categoryAlternative?.itemId !== item.id) {
      setCategoryAlternative({ itemId: item.id, candidate: null })
      void getMyPricesCategorySuggestions(item.id)
        .then((candidates) =>
          setCategoryAlternative({
            itemId: item.id,
            candidate: candidates.find((candidate) => candidate.productTypeId !== item.productTypeId) ?? null,
          }),
        )
        .catch(() => undefined)
    }
  }

  const saveInline = async (
    item: SupplierCatalogItemDto,
    message: string,
    save: () => Promise<SupplierCatalogItemDto>,
    undo: (() => Promise<SupplierCatalogItemDto>) | null,
  ) => {
    try {
      handleItemSaved(await save())
      showToast(
        message,
        undo
          ? {
              label: 'Отменить',
              onAction: () => {
                void undo()
                  .then(handleItemSaved)
                  .catch((undoError) =>
                    setError(undoError instanceof Error ? undoError.message : 'Не удалось отменить правку'),
                  )
              },
            }
          : undefined,
      )
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : `Не удалось сохранить «${item.supplierName}»`)
    }
  }

  const commitEdit = (
    item: SupplierCatalogItemDto,
    column: MyPricesEditableColumn,
    value: string | null,
    next: CellEditTarget | null = null,
  ) => {
    setEditing(next)
    if (value == null) return
    const text = value.trim()
    // Прежние значения — на момент правки: к отмене строка может прийти уже обновлённой.
    const previous = { name: item.supplierName, price: item.price, unit: item.unit, supplierId: item.privateSupplierId }
    switch (column) {
      case 'name':
        if (!text || text === item.supplierName) return
        void saveInline(
          item,
          'Название сохранено',
          () => updateRestaurantMyPricesItemFields(item.id, { name: text }),
          () => updateRestaurantMyPricesItemFields(item.id, { name: previous.name }),
        )
        return
      case 'price': {
        const price = parsePrice(text)
        if (price == null) {
          if (text) showToast('Цена должна быть числом')
          return
        }
        if (price === item.price) return
        void saveInline(
          item,
          'Цена сохранена',
          () => updateRestaurantMyPricesItemFields(item.id, { price }),
          () => updateRestaurantMyPricesItemFields(item.id, { price: previous.price }),
        )
        return
      }
      case 'unit':
        if (!text || text === formatUnitLabel(item.unit)) return
        void saveInline(
          item,
          'Единица сохранена',
          () => updateRestaurantMyPricesItemFields(item.id, { unit: text }),
          () => updateRestaurantMyPricesItemFields(item.id, { unit: previous.unit }),
        )
        return
      case 'supplier': {
        const supplierId = Number(text)
        if (!supplierId || supplierId === item.privateSupplierId) return
        const previousSupplierId = previous.supplierId
        void saveInline(
          item,
          'Поставщик сохранён',
          () => updateRestaurantMyPricesItemFields(item.id, { privateSupplierId: supplierId }),
          previousSupplierId != null
            ? () => updateRestaurantMyPricesItemFields(item.id, { privateSupplierId: previousSupplierId })
            : null,
        )
        return
      }
      case 'category': {
        const typeId = Number(text)
        if (!typeId) return
        if (typeId === item.productTypeId) {
          if (needsAttention(item)) void handleConfirmMatch(item)
          return
        }
        // Отмены нет: возврат прежней категории не вернул бы статус «предложено».
        void saveInline(
          item,
          'Категория сохранена',
          () => assignRestaurantMyPricesProductType(item.id, typeId),
          null,
        )
        return
      }
    }
  }

  const nameSuggestionGroups = async (item: SupplierCatalogItemDto, query: string): Promise<CellEditorGroup[]> => {
    const suggestions = (await getMyPricesNameSuggestions(organizationId, query)).filter(
      (suggestion) => suggestion.name !== item.supplierName,
    )
    return suggestions.length > 0
      ? [
          {
            label: 'Похожие в базе',
            options: suggestions.map((suggestion) => ({
              value: suggestion.name,
              label: suggestion.name,
              meta: suggestion.productTypeName,
            })),
          },
        ]
      : []
  }

  const renderCellEditor = (item: SupplierCatalogItemDto, column: MyPricesEditableColumn) => {
    const visibleIds = columnPrefs.visibleColumns.map((visible) => visible.id)
    const onTab = (direction: 1 | -1, value: string | null) =>
      commitEdit(item, column, value, { itemId: item.id, column: neighbourColumn(column, direction, visibleIds) })
    const common = {
      anchor: editAnchor,
      onCommit: (value: string) => commitEdit(item, column, value),
      onCancel: () => setEditing(null),
      onTab,
    }
    switch (column) {
      case 'name':
        return (
          <CellEditor
            {...common}
            kind="text"
            ariaLabel="Наименование"
            initialText={item.supplierName}
            loadGroups={(query) => nameSuggestionGroups(item, query)}
          />
        )
      case 'price':
        return (
          <CellEditor
            {...common}
            kind="number"
            ariaLabel="Цена, ₽"
            initialText={item.price != null ? String(item.price).replace('.', ',') : ''}
          />
        )
      case 'unit':
        return (
          <CellEditor
            {...common}
            kind="list"
            searchable={false}
            ariaLabel="Единица измерения"
            initialText={formatUnitLabel(item.unit)}
            currentValue={formatUnitLabel(item.unit)}
            loadGroups={unitGroups}
          />
        )
      case 'supplier':
        return (
          <CellEditor
            {...common}
            kind="list"
            ariaLabel="Поставщик"
            placeholder="Найти поставщика"
            initialText={item.privateSupplierName ?? ''}
            currentValue={item.privateSupplierId != null ? String(item.privateSupplierId) : null}
            loadGroups={(query) => supplierGroups(privateSuppliers, query)}
          />
        )
      case 'category': {
        const alternative = categoryAlternative?.itemId === item.id ? categoryAlternative.candidate : null
        return (
          <CellEditor
            {...common}
            kind="list"
            ariaLabel="Категория"
            placeholder="Найти категорию"
            initialText={item.productTypeName ?? ''}
            currentValue={item.productTypeId != null ? String(item.productTypeId) : null}
            optionsKey={alternative ? String(alternative.productTypeId) : ''}
            loadGroups={(query) => categoryGroups(productTypes, query, item.productTypeId, alternative)}
          />
        )
      }
    }
  }

  const handleQuickDelete = async (item: SupplierCatalogItemDto) => {
    setDeletingId(item.id)
    setError(null)
    try {
      if (isRestaurantMyPrices) {
        await deleteRestaurantMyPricesItem(item.id)
      } else {
        await deleteCatalogItem(item.id)
      }
      if (expandedId === item.id) {
        setExpandedId(null)
      }
      setSelectedIds((current) => current.filter((id) => id !== item.id))
      handleItemDeleted()
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Не удалось удалить позицию')
    } finally {
      setDeletingId(null)
    }
  }

  const handleBulkDelete = async (itemIds: number[]) => {
    if (itemIds.length === 0) {
      return
    }
    try {
      if (isRestaurantMyPrices) {
        await bulkDeleteRestaurantMyPricesItems({
          organizationId,
          itemIds,
        })
      } else {
        await bulkDeleteCatalogItems({
          organizationId,
          itemIds,
        })
      }
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Не удалось удалить позиции')
    }
  }

  const requestBulkDelete = () => {
    if (selectedIds.length > 0) {
      setDeleteConfirmation({ kind: 'selection', itemIds: selectedIds })
    }
  }

  const confirmDelete = () => {
    const pendingDelete = deleteConfirmation
    if (!pendingDelete) return

    setDeleteConfirmation(null)
    if (pendingDelete.kind === 'item') {
      void handleQuickDelete(pendingDelete.item)
      return
    }
    void handleBulkDelete(pendingDelete.itemIds)
  }

  const handleRematch = async () => {
    if (
      !window.confirm(
        'Пересчитать матчинг для всех активных позиций? Ручные правки категорий и брендов будут сохранены.',
      )
    ) {
      return
    }
    setRematching(true)
    setError(null)
    setRematchMessage(null)
    try {
      const result = await rematchSupplierCatalog(organizationId)
      setRematchMessage(
        `Пересчитано: ${result.rematchedCount}, пропущено вручную: ${result.skippedManualCount}`,
      )
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (rematchError) {
      setError(rematchError instanceof Error ? rematchError.message : 'Не удалось пересчитать матчинг')
    } finally {
      setRematching(false)
    }
  }

  // Дозаполнение и сведение — два прохода по разным позициям (пустой бренд против уже
  // проставленного), но оператору полезны только вместе и строго в этом порядке: иначе
  // только что дозаполненные марки останутся с разнобоем в написании.
  const handleTidyBrands = async () => {
    if (
      !window.confirm(
        'Привести бренды в порядок? У позиций без бренда марка определится по названию, разные написания одной марки объединятся, значения, которые брендом не являются, будут очищены. Ручные правки сохранятся, категории не пересчитываются.',
      )
    ) {
      return
    }
    setTidyingBrands(true)
    setError(null)
    setRematchMessage(null)
    try {
      const backfill = await backfillCatalogBrands(organizationId)
      const normalize = await normalizeCatalogBrands(organizationId)
      setRematchMessage(
        `Дозаполнено пустых: ${backfill.filled}, объединено написаний: ${normalize.canonicalized}, ` +
          `очищено не-брендов: ${normalize.cleared}, защищено от перезаписи прайсом: ${normalize.protectedFromReimport}`,
      )
      loadFilterOptions()
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (tidyError) {
      setError(tidyError instanceof Error ? tidyError.message : 'Не удалось привести бренды в порядок')
    } finally {
      setTidyingBrands(false)
    }
  }

  const handleCreateItem = async () => {
    if (!newItemName.trim()) {
      return
    }
    setCreatingItem(true)
    setError(null)
    try {
      const created = await createCatalogItem({
        organizationId,
        name: newItemName,
        brand: newItemBrand || null,
        externalSku: newItemSku || null,
      })

      const priceValue = newItemPrice.trim() ? Number(newItemPrice.replace(',', '.')) : null
      const minShipmentValue = newItemMinShipmentQty.trim()
        ? Number(newItemMinShipmentQty.replace(',', '.'))
        : null
      const country = newItemCountry.trim() || null
      const unit = newItemUnit.trim() || null
      const packageSize = newItemPackageSize.trim() || null

      if (
        country != null ||
        priceValue != null ||
        unit != null ||
        minShipmentValue != null ||
        packageSize != null
      ) {
        await updateCatalogItemFields(created.id, {
          country,
          price: priceValue,
          unit,
          minShipmentQty: minShipmentValue,
          packageSize,
        })
      }

      if (newItemProductTypeId != null) {
        await assignCatalogProductType(created.id, newItemProductTypeId)
      }

      resetCreateForm()
      setShowCreateForm(false)
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Не удалось добавить позицию')
    } finally {
      setCreatingItem(false)
    }
  }

  const myPricesEmptyKind =
    isRestaurantMyPrices && summary
      ? getMyPricesEmptyState({
          totalItems: summary.totalItems,
          needsAttention: summary.needsAttention,
          attentionOnly,
        })
      : null
  const myPricesEmptyState =
    myPricesEmptyKind === 'all-checked'
      ? {
          title: 'Все категории проверены',
          description: 'В прайсах больше нет позиций, требующих внимания.',
          actions: [{ label: 'Показать все позиции', onClick: toggleAttention }],
        }
      : myPricesEmptyKind === 'filtered'
        ? {
            title: 'По выбранным фильтрам позиций нет',
            description: 'Измените условия поиска или сбросьте фильтры, чтобы увидеть все позиции.',
            actions: [{ label: 'Сбросить фильтры', onClick: handleResetSearch }],
          }
        : null

  return (
    <section
      className={
        isRestaurantMyPrices
          ? 'market-page market-page--wide market-page--my-prices'
          : 'market-page market-page--wide'
      }
    >
      <div className="market-page__intro">
        {backLink ? <nav className="market-page__breadcrumb">{backLink}</nav> : null}

        <MarketPageHeader
          title={title}
          description={headerDescription}
          actions={
            <>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                onClick={() => navigate(importPath)}
              >
                Импорт прайса
              </button>
              {isRestaurantMyPrices ? (
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => navigate('/my-prices/suppliers')}
                >
                  Поставщики
                </button>
              ) : null}
              {!isRestaurantMyPrices ? (
                <>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost"
                    onClick={() => setShowCreateForm((current) => !current)}
                  >
                    {showCreateForm ? 'Скрыть форму' : 'Добавить позицию'}
                  </button>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost"
                    disabled={tidyingBrands || rematching || loading}
                    onClick={() => void handleTidyBrands()}
                  >
                    {tidyingBrands ? 'Разбор брендов…' : 'Привести бренды в порядок'}
                  </button>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost"
                    disabled={rematching || tidyingBrands || loading}
                    onClick={() => void handleRematch()}
                  >
                    {rematching ? 'Пересчёт…' : 'Пересчитать всё'}
                  </button>
                </>
              ) : null}
            </>
          }
        />

        {showCatalogVersions ? (
          <CatalogVersionHistoryPanel
            organizationId={organizationId}
            mode={isRestaurantMyPrices ? 'buyer-private-supplier' : 'supplier'}
            privateSupplierId={privateSupplierId}
            onRestored={() =>
              void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
            }
          />
        ) : null}

        {isRestaurantMyPrices ? (
          <div className="market-my-prices-intro">
            {summary ? (
              <p className="market-my-prices-summary">
                <span className="market-my-prices-summary__total">{summary.totalItems} поз.</span>
                {summary.totalItems > 0 ? (
                  <AttentionChip
                    count={summary.needsAttention}
                    active={attentionOnly}
                    onToggle={toggleAttention}
                  />
                ) : null}
              </p>
            ) : null}

            {duplicateGroups.length > 0 ? (
              <DuplicateNotice groupCount={duplicateGroups.length} onOpen={() => setShowDuplicates(true)} />
            ) : null}
          </div>
        ) : (
          <div
            className={`market-catalog-intro-split${
              summary ? '' : ' market-catalog-intro-split--hint-only'
            }`}
          >
            <p className="market-hint market-hint--in-split">
              Загрузите прайс через импорт — система сопоставит позиции с категориями Провизиум. Проверьте
              строки, которые требуют внимания или без типа, и при необходимости назначьте категорию вручную.
            </p>

            {summary ? (
              <article className="echat-panel market-catalog-summary market-catalog-summary--inline">
                <p className="echat-panel__text market-catalog-summary__headline">
                  <span>Всего позиций: {summary.totalItems}</span>
                  {summary.needsAttention > 0 ? (
                    <span className="market-catalog-summary__attention">
                      Требуют внимания: {summary.needsAttention}
                    </span>
                  ) : null}
                </p>
                <p className="echat-muted">
                  Определены автоматически: {summary.matchAuto} · Требуют внимания:{' '}
                  {summary.matchSuggested} · Заданы вручную: {summary.matchManual} · Без типа:{' '}
                  {summary.matchUnmatched}
                </p>
                {!isRestaurantMyPrices && (summary.staleItems > 0 || summary.hiddenFromSearch > 0) ? (
                  <p className="market-catalog-summary__freshness">
                    {summary.staleItems > 0
                      ? `Без обновления более 7 дн.: ${summary.staleItems}`
                      : null}
                    {summary.staleItems > 0 && summary.hiddenFromSearch > 0 ? ' · ' : null}
                    {summary.hiddenFromSearch > 0
                      ? `Скрыто из выдачи: ${summary.hiddenFromSearch}`
                      : null}
                  </p>
                ) : null}
              </article>
            ) : null}
            {duplicateGroups.length > 0 ? (
              <DuplicateNotice groupCount={duplicateGroups.length} onOpen={() => setShowDuplicates(true)} />
            ) : null}
          </div>
        )}
      </div>

      {showDuplicates && duplicateGroups.length > 0 ? (
        <CatalogDuplicateMergePanel
          organizationId={organizationId}
          groups={duplicateGroups}
          catalogMode={catalogMode}
          onDismiss={() => setShowDuplicates(false)}
          onNotDuplicates={(group) => {
            void (async () => {
              const itemIds = group.items.map((item) => item.id)
              try {
                if (isRestaurantMyPrices) {
                  await dismissMyPricesDuplicates({ organizationId, itemIds })
                } else {
                  await dismissCatalogDuplicates({ organizationId, itemIds })
                }
                const rest = duplicateGroups.filter((item) => item !== group)
                setDuplicateGroups(rest)
                if (rest.length === 0) {
                  setShowDuplicates(false)
                }
              } catch (dismissError) {
                setError(
                  dismissError instanceof Error
                    ? dismissError.message
                    : 'Не удалось сохранить решение «это не дубликаты»',
                )
              }
            })()
          }}
          onMerged={async () => {
            await load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
          }}
        />
      ) : null}

      {!isRestaurantMyPrices && showCreateForm ? (
        <article className="echat-panel market-catalog-create">
          <h2 className="echat-panel__subtitle">Новая позиция</h2>
          <div className="market-catalog-edit__grid">
            <label className="echat-field">
              <span className="echat-field__label">Наименование</span>
              <input
                className="echat-field__input"
                value={newItemName}
                required
                onChange={(event) => setNewItemName(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Артикул</span>
              <input
                className="echat-field__input"
                value={newItemSku}
                onChange={(event) => setNewItemSku(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Цена</span>
              <input
                className="echat-field__input"
                inputMode="decimal"
                value={newItemPrice}
                onChange={(event) => setNewItemPrice(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Ед. изм.</span>
              <input
                className="echat-field__input"
                value={newItemUnit}
                onChange={(event) => setNewItemUnit(event.target.value)}
              />
            </label>
            <SuggestTextField
              label="Бренд"
              value={newItemBrand}
              suggestions={brandOptions}
              listId="catalog-create-brand"
              onChange={setNewItemBrand}
            />
            <SuggestTextField
              label="Страна"
              value={newItemCountry}
              suggestions={countryOptions}
              listId="catalog-create-country"
              onChange={setNewItemCountry}
            />
            <label className="echat-field">
              <span className="echat-field__label">Мин. отгрузка</span>
              <input
                className="echat-field__input"
                inputMode="decimal"
                value={newItemMinShipmentQty}
                onChange={(event) => setNewItemMinShipmentQty(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Упаковка</span>
              <input
                className="echat-field__input"
                value={newItemPackageSize}
                onChange={(event) => setNewItemPackageSize(event.target.value)}
              />
            </label>
            <ProductTypeCascadePicker
              productTypes={productTypes}
              value={newItemProductTypeId}
              onChange={setNewItemProductTypeId}
              className="market-product-type-cascade market-product-type-cascade--form"
              idPrefix="catalog-create"
            />
          </div>

          {loadingCreateSuggestions ? <p className="echat-muted">Загрузка подсказок…</p> : null}
          {createCandidates.length > 0 ? (
            <div className="market-catalog-edit__suggestions">
              <p className="echat-field__label">Подсказки матчера</p>
              <div className="market-inline-actions">
                {createCandidates.slice(0, 5).map((candidate) => (
                  <button
                    key={candidate.productTypeId}
                    type="button"
                    className="echat-btn echat-btn--ghost echat-btn--sm"
                    onClick={() => setNewItemProductTypeId(candidate.productTypeId)}
                  >
                    {candidate.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="market-inline-actions">
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={creatingItem || !newItemName.trim()}
              onClick={() => void handleCreateItem()}
            >
              {creatingItem ? 'Сохранение…' : 'Создать'}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={creatingItem}
              onClick={() => {
                resetCreateForm()
                setShowCreateForm(false)
              }}
            >
              Отмена
            </button>
          </div>
        </article>
      ) : null}

      <div
        className={
          isRestaurantMyPrices
            ? 'market-filters market-filters--my-prices'
            : 'market-filters market-filters--supplier-catalog'
        }
      >
        {isRestaurantMyPrices ? (
          <>
            <div className="market-filters__row market-filters__row--primary">
              <label className="echat-field market-filters__field market-filters__field--search">
                <span className="echat-field__label">Поиск</span>
                <span className="market-filter-search-control">
                  <SearchIcon />
                  <input
                    className="echat-field__input"
                    value={searchDraft}
                    placeholder="Название или бренд"
                    onChange={(event) => setSearchDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleSearch()
                      }
                    }}
                  />
                </span>
              </label>

              <ProductTypeCascadePicker
                productTypes={productTypes}
                value={productTypeFilter}
                onChange={setProductTypeFilter}
                compact
                emptyLabel="Все"
                disabled={unassignedOnly}
                showCascadeLevels={false}
                quickSearchClassName="market-filters__field market-filters__field--quick-search"
                idPrefix="my-prices-filter"
              />

              <div className="market-filters__actions">
                <span className="echat-field__label market-filters__actions-label" aria-hidden="true">
                  &nbsp;
                </span>
                <div className="market-filters__actions-row">
                  <TableColumnSettings prefs={columnPrefs} />
                  <TableDensityToggle density={density} onChange={setDensity} />
                  <button type="button" className="echat-btn echat-btn--primary" onClick={handleSearch}>
                    Найти
                  </button>
                  <button type="button" className="echat-btn echat-btn--ghost" onClick={handleResetSearch}>
                    Сбросить
                  </button>
                  {selectedIds.length > 0 ? (
                    <>
                      {isRestaurantMyPrices ? (
                        <button
                          type="button"
                          className="echat-btn echat-btn--ghost"
                          onClick={() => setBulkListDialogOpen(true)}
                          disabled={listUpdatingKey != null || catalogLists.length === 0}
                        >
                          <IconListPlus />
                          В список ({selectedIds.length})
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="echat-btn echat-btn--primary"
                        onClick={handleBulkAddToCart}
                      >
                        В заявку ({selectedIds.length})
                      </button>
                      {acceptableSelected.length > 0 ? (
                        <button
                          type="button"
                          className="echat-btn echat-btn--ghost"
                          disabled={acceptingCategories}
                          onClick={() => void handleAcceptCategories()}
                        >
                          Принять категории ({acceptableSelected.length})
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="echat-btn echat-btn--ghost"
                        onClick={handleBulkEdit}
                        disabled={selectedIds.length !== 1}
                        title={
                          selectedIds.length === 1
                            ? 'Редактировать выбранную позицию'
                            : 'Выберите одну позицию'
                        }
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="echat-btn echat-btn--danger-soft"
                        onClick={requestBulkDelete}
                      >
                        Удалить ({selectedIds.length})
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="market-page__filter-bar">
              <div className="market-filters__persistent-controls">
                <SelectCombobox
                  className="market-filters__field market-filters__field--supplier"
                  label="Поставщик"
                  value={privateSupplierId != null ? String(privateSupplierId) : ''}
                  options={[
                    { value: '', label: 'Все' },
                    ...privateSuppliers.map((supplier) => ({
                      value: String(supplier.id),
                      label: `${supplier.legalName}${supplier.ogrn ? ` · ${ogrnLabel(supplier.ogrn)} ${supplier.ogrn}` : supplier.inn ? ` · ИНН ${supplier.inn}` : ''}`,
                    })),
                  ]}
                  onChange={(next) => setPrivateSupplierId(next ? Number(next) : null)}
                />

                <SelectCombobox
                  className="market-filters__field market-filters__field--list"
                  label="Мои списки"
                  value={catalogListId != null ? String(catalogListId) : ''}
                  options={[
                    { value: '', label: 'Все позиции' },
                    ...catalogLists.map((list) => ({ value: String(list.id), label: list.name })),
                    { value: ADD_CATALOG_LIST_OPTION, label: 'Добавить список', icon: <IconListPlus /> },
                  ]}
                  onChange={(next) => {
                    if (next === ADD_CATALOG_LIST_OPTION) {
                      setListManagerOpen(true)
                      return
                    }
                    setCatalogListId(next ? Number(next) : null)
                  }}
                />

                <label className="echat-field market-filters__checkbox market-filters__checkbox--inline">
                  <span className="echat-field__label" aria-hidden="true">&nbsp;</span>
                  <span className="market-filters__checkbox-control">
                    <input
                      type="checkbox"
                      checked={favoriteList != null && catalogListId === favoriteList.id}
                      disabled={favoriteList == null}
                      onChange={(event) => setCatalogListId(event.target.checked ? favoriteList?.id ?? null : null)}
                    />
                    <span>Только избранное</span>
                  </span>
                </label>
              </div>

              <button
                type="button"
                className="market-filters__more-toggle"
                aria-expanded={showMoreFilters}
                aria-controls="my-prices-more-filters"
                onClick={() => setShowMoreFilters((current) => !current)}
              >
                <FiltersIcon />
                {showMoreFilters ? 'Скрыть фильтры' : 'Ещё фильтры'}
                <svg
                  className={`market-filters__more-chevron${showMoreFilters ? ' market-filters__more-chevron--open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div
              id="my-prices-more-filters"
              className={`market-filters__more${showMoreFilters ? ' is-open' : ''}`}
              hidden={!showMoreFilters}
            >
              <ProductTypeCascadePicker
                productTypes={productTypes}
                value={productTypeFilter}
                onChange={setProductTypeFilter}
                compact
                emptyLabel="Не выбран"
                disabled={unassignedOnly}
                showQuickSearch={false}
                className="market-product-type-cascade market-product-type-cascade--levels-only"
                idPrefix="my-prices-filter-more"
              />
            </div>
          </>
        ) : (
          <>
            <div className="market-page__filter-bar">
              <button
                type="button"
                className="market-filters__more-toggle"
                aria-expanded={showMoreFilters}
                aria-controls="supplier-catalog-more-filters"
                onClick={() => setShowMoreFilters((current) => !current)}
              >
                <FiltersIcon />
                {showMoreFilters ? 'Скрыть фильтры' : 'Ещё фильтры'}
                <svg
                  className={`market-filters__more-chevron${showMoreFilters ? ' market-filters__more-chevron--open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <ProductTypeCascadePicker
              productTypes={productTypes}
              value={productTypeFilter}
              onChange={setProductTypeFilter}
              compact
              emptyLabel="Все"
              disabled={unassignedOnly}
              idPrefix="catalog-filter"
            />

            <label className="echat-field market-filters__field market-filters__field--search">
              <span className="echat-field__label">Поиск</span>
              <span className="market-filter-search-control">
                <SearchIcon />
                <input
                  className="echat-field__input"
                  value={searchDraft}
                  placeholder="Название или бренд"
                  onChange={(event) => setSearchDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch()
                    }
                  }}
                />
              </span>
            </label>

            <div className="market-filters__actions">
              <TableColumnSettings prefs={columnPrefs} />
              <TableDensityToggle density={density} onChange={setDensity} />
              <button type="button" className="echat-btn echat-btn--primary" onClick={handleSearch}>
                Найти
              </button>
              <button type="button" className="echat-btn echat-btn--ghost" onClick={handleResetSearch}>
                Сбросить
              </button>
              {selectedIds.length > 0 ? (
                <button
                  type="button"
                  className="echat-btn echat-btn--danger-soft"
                  onClick={requestBulkDelete}
                >
                  Удалить ({selectedIds.length})
                </button>
              ) : null}
            </div>

            <div
              id="supplier-catalog-more-filters"
              className={`market-filters__more${showMoreFilters ? ' is-open' : ''}`}
              hidden={!showMoreFilters}
            >
              <SelectCombobox
                className="market-filters__field market-filters__field--status"
                label="Тип определения"
                value={matchStatus}
                options={[
                  { value: '', label: 'Все' },
                  { value: 'auto', label: matchDeterminationLabel('auto') },
                  { value: 'suggested', label: matchDeterminationLabel('suggested') },
                  { value: 'manual', label: matchDeterminationLabel('manual') },
                  { value: 'unmatched', label: matchDeterminationLabel('unmatched') },
                ]}
                onChange={setMatchStatus}
              />

              <label className="echat-field market-filters__checkbox market-filters__checkbox--inline">
                <input
                  type="checkbox"
                  checked={unassignedOnly}
                  onChange={(event) => {
                    setUnassignedOnly(event.target.checked)
                    if (event.target.checked) {
                      setProductTypeFilter(null)
                    }
                  }}
                />
                <span>Только без категории</span>
              </label>
            </div>
          </>
        )}
      </div>

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {rematchMessage ? <p className="echat-muted">{rematchMessage}</p> : null}

      {attentionOnly && summary && summary.needsAttention > 0 ? (
        <p className="market-attention-note" role="status">
          Показаны позиции, где категория под вопросом. Подтвердите галочкой или нажмите на категорию,
          чтобы выбрать другую, — строка уйдёт из списка.{' '}
          <button type="button" className="market-attention-note__link" onClick={toggleAttention}>
            Показать все
          </button>
        </p>
      ) : null}

      {page && page.items.length > 0 ? (
        <>
        <DataTable
          className={['market-table--expandable market-table--cards market-table--configurable', tableClass].filter(Boolean).join(' ')}
          wrapClassName="market-table-wrap--cards"
          caption={isRestaurantMyPrices ? 'Мои прайсы' : 'Каталог поставщика'}
        >
            <colgroup>
              {columnPrefs.visibleColumns.map((column) => (
                <col
                  key={column.id}
                  style={
                    columnPrefs.getWidth(column.id) != null
                      ? { width: columnPrefs.getWidth(column.id) }
                      : undefined
                  }
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columnPrefs.visibleColumns.map((column) => {
                  const sortKey = isCatalogItemSortKey(column.id) ? column.id : null
                  const sortDirection = sortKey ? getDirection(sortKey) : null
                  return (
                    <ResizableTh
                      key={column.id}
                      columnId={column.id}
                      label={column.label}
                      width={columnPrefs.getWidth(column.id)}
                      minWidth={columnPrefs.getMinWidth(column.id)}
                      className={
                        column.id === 'select'
                          ? 'market-table__col--check'
                          : column.id === 'actions'
                            ? 'market-table__cell--action'
                            : column.id === 'name'
                              ? 'market-table__cell--primary'
                              : undefined
                      }
                      onResize={columnPrefs.setWidth}
                    >
                      {column.id === 'select' || column.id === 'actions' ? null : sortKey ? (
                        <button
                          type="button"
                          className={sortDirection ? 'market-table__sort is-active' : 'market-table__sort'}
                          onClick={() => toggleSort(sortKey)}
                          aria-label={
                            sortDirection
                              ? `${column.label}, сейчас ${sortDirection === 'asc' ? 'по возрастанию' : 'по убыванию'}`
                              : `Сортировать по столбцу ${column.label}`
                          }
                          title="Сортировать"
                        >
                          <span className="market-table__sort-label">{column.label}</span>
                          <span className="market-table__sort-icon" aria-hidden="true">
                            {sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '↕'}
                          </span>
                        </button>
                      ) : (
                        column.label
                      )}
                    </ResizableTh>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {page.items.map((item, index) => {
                const expanded = expandedId === item.id
                const selected = selectedIds.includes(item.id)
                const stripeClass = index % 2 === 0 ? 'market-table__row--odd' : 'market-table__row--even'
                const rowClassName = [
                  'market-catalog-row',
                  stripeClass,
                  expanded ? 'market-table__row--expanded' : null,
                  selected ? 'market-table__row--selected' : null,
                ]
                  .filter(Boolean)
                  .join(' ')
                const freshnessClass = item.freshnessStatus
                  ? `market-catalog-freshness market-catalog-freshness--${item.freshnessStatus.replace('_', '-')}`
                  : 'market-catalog-freshness'
                const hint = freshnessHint(item, !isRestaurantMyPrices)
                return (
                  <Fragment key={item.id}>
                    <tr
                      className={rowClassName || undefined}
                      onClick={() => toggleSelected(item.id)}
                      onKeyDown={(event) => {
                        // Нажатия из редактора ячейки всплывают сюда через портал: без этой
                        // проверки пробел не вводился в название, а Enter заодно отмечал строку.
                        if (event.target !== event.currentTarget) return
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          toggleSelected(item.id)
                        }
                      }}
                      tabIndex={0}
                      aria-selected={selected}
                      aria-label={`${item.supplierName}, ${selected ? 'выбрано' : 'не выбрано'}${hint ? `, ${hint}` : ''}`}
                    >
                      {columnPrefs.visibleColumns.map((column) => {
                        switch (column.id) {
                          case 'select':
                            return (
                              <td
                                key={column.id}
                                className="market-table__col--check"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  aria-label={`Выбрать ${item.supplierName}`}
                                  checked={selected}
                                  onChange={() => toggleSelected(item.id)}
                                />
                              </td>
                            )
                          case 'name':
                            return (
                              <td
                                key={column.id}
                                data-label="Позиция"
                                {...editableCellProps(item, 'name', 'market-table__cell--primary')}
                              >
                                {isEditing(item, 'name') ? renderCellEditor(item, 'name') : item.supplierName}
                              </td>
                            )
                          case 'supplier':
                            return (
                              <td key={column.id} data-label="Поставщик" {...editableCellProps(item, 'supplier')}>
                                {isEditing(item, 'supplier')
                                  ? renderCellEditor(item, 'supplier')
                                  : (item.privateSupplierName ?? '—')}
                              </td>
                            )
                          case 'sku':
                            return (
                              <td key={column.id} data-label="Артикул">
                                {item.externalSku?.trim() || '—'}
                              </td>
                            )
                          case 'price': {
                            if (!isRestaurantMyPrices) {
                              return (
                                <td key={column.id} data-label="Цена" className="market-table__cell--price">
                                  {formatCatalogPrice(item)}
                                </td>
                              )
                            }
                            const deltaPct = item.priceDeltaPct
                            const previous = item.previousPrice
                            const hasChange =
                              previous != null && deltaPct != null && Number(deltaPct) !== 0
                            const unchanged = previous != null && (deltaPct == null || Number(deltaPct) === 0)
                            const deltaAbs =
                              item.price != null && previous != null ? item.price - previous : 0
                            if (isEditing(item, 'price')) {
                              return (
                                <td
                                  key={column.id}
                                  data-label="Цена"
                                  {...editableCellProps(item, 'price', 'market-table__cell--price market-my-prices-price')}
                                >
                                  {renderCellEditor(item, 'price')}
                                </td>
                              )
                            }
                            return (
                              <td
                                key={column.id}
                                data-label="Цена"
                                {...editableCellProps(item, 'price', 'market-table__cell--price market-my-prices-price')}
                              >
                                <div className="market-my-prices-price__row">
                                  <span className="market-my-prices-price__current">
                                    {formatCatalogPriceRub(item.price)}
                                  </span>
                                  {hasChange ? (
                                    <span className={priceMoveToneClass(deltaAbs)}>
                                      {deltaAbs < 0 ? '↓' : '↑'}{' '}
                                      {formatAnalyticsPct(deltaPct).replace('.', ',')}
                                    </span>
                                  ) : null}
                                  {unchanged ? (
                                    <span className="market-analytics-delta">без изм.</span>
                                  ) : null}
                                </div>
                              </td>
                            )
                          }
                          case 'unit':
                            return (
                              <td key={column.id} data-label="Ед. изм." {...editableCellProps(item, 'unit')}>
                                {isEditing(item, 'unit')
                                  ? renderCellEditor(item, 'unit')
                                  : item.unit
                                    ? formatUnitLabel(item.unit)
                                    : '—'}
                              </td>
                            )
                          case 'package':
                            return (
                              <td key={column.id} data-label="Фасовка">
                                {item.packageSize ?? '—'}
                              </td>
                            )
                          case 'minShipment':
                            return (
                              <td key={column.id} data-label="Мин. отгрузка">
                                {item.minShipmentQty != null ? String(item.minShipmentQty) : '—'}
                              </td>
                            )
                          case 'brand':
                            return (
                              <td key={column.id} data-label="Бренд">
                                {item.brand ?? '—'}
                              </td>
                            )
                          case 'country':
                            return (
                              <td key={column.id} data-label="Страна">
                                {item.country ?? '—'}
                              </td>
                            )
                          case 'category': {
                            const categoryLabel = formatProductTypeLabel(
                              item.productTypeId,
                              item.productTypeName,
                              item.productTypeActive,
                              typeNames,
                            )
                            const doubtful = isRestaurantMyPrices && needsAttention(item)
                            return (
                              <td key={column.id} data-label="Категория" {...editableCellProps(item, 'category')}>
                                {isEditing(item, 'category') ? (
                                  renderCellEditor(item, 'category')
                                ) : doubtful ? (
                                  <span className="market-attention-category" title="Категория под вопросом — проверьте">
                                    <span className="market-attention-dot" aria-hidden="true" />
                                    {item.productTypeId != null ? categoryLabel : <span className="echat-muted">Не определена</span>}
                                    <span className="visually-hidden">, требует внимания</span>
                                  </span>
                                ) : (
                                  categoryLabel
                                )}
                              </td>
                            )
                          }
                          case 'updated': {
                            const priceMoved =
                              isRestaurantMyPrices &&
                              item.previousPrice != null &&
                              item.priceDeltaPct != null &&
                              Number(item.priceDeltaPct) !== 0
                            const priceUp =
                              priceMoved &&
                              item.price != null &&
                              item.previousPrice != null &&
                              item.price > item.previousPrice
                            return (
                              <td key={column.id} data-label="Обновление">
                                <div className={freshnessClass}>
                                  <span className="market-catalog-freshness__value">
                                    {formatUpdatedAt(item.updatedAt)}
                                  </span>
                                  {priceMoved ? (
                                    <span
                                      className={
                                        priceUp
                                          ? 'market-catalog-freshness__hint market-catalog-freshness__hint--up'
                                          : 'market-catalog-freshness__hint market-catalog-freshness__hint--down'
                                      }
                                    >
                                      цена изменилась
                                    </span>
                                  ) : hint ? (
                                    <span className="market-catalog-freshness__hint">{hint}</span>
                                  ) : null}
                                </div>
                              </td>
                            )
                          }
                          case 'match':
                            return (
                              <td key={column.id} data-label="Тип определения">
                                {matchDeterminationLabel(item.matchStatus)}
                              </td>
                            )
                          case 'actions':
                            {
                              const selectedListIds = item.catalogListIds ?? []
                              const isFavorite = favoriteList != null && selectedListIds.includes(favoriteList.id)
                              const favoriteUpdateKey = favoriteList ? `${item.id}:${favoriteList.id}` : ''
                            return (
                              <td
                                key={column.id}
                                className="market-table__cell--action"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <div className="market-table__row-actions">
                                  {(isRestaurantMyPrices
                                    ? needsAttention(item)
                                    : item.matchStatus.toLowerCase() === 'suggested'
                                      || item.matchStatus.toLowerCase() === 'auto')
                                  && item.productTypeId != null ? (
                                    <button
                                      type="button"
                                      className="echat-btn echat-btn--ghost market-table__icon-btn market-table__icon-btn--confirm"
                                      aria-label={`Подтвердить категорию для ${item.supplierName}`}
                                      title={
                                        isRestaurantMyPrices
                                          ? 'Подтвердить категорию'
                                          : item.matchStatus.toLowerCase() === 'auto'
                                            ? 'Подтвердить авто'
                                            : 'Подтвердить'
                                      }
                                      disabled={confirmingId === item.id || loading}
                                      onClick={() => void handleConfirmMatch(item)}
                                    >
                                      <IconCheck />
                                    </button>
                                  ) : null}
                                  {isRestaurantMyPrices ? (
                                    <button
                                      type="button"
                                      className={`echat-btn echat-btn--ghost market-table__icon-btn${isFavorite ? ' market-table__icon-btn--favorite' : ''}`}
                                      aria-label={`${isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}: ${item.supplierName}`}
                                      title={isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}
                                      disabled={!favoriteList || listUpdatingKey === favoriteUpdateKey}
                                      onClick={() => {
                                        if (favoriteList) {
                                          void handleListToggle(item, favoriteList.id, !isFavorite)
                                        }
                                      }}
                                    >
                                      <IconStar />
                                    </button>
                                  ) : null}
                                  {isRestaurantMyPrices ? (
                                    <button
                                      type="button"
                                      className="echat-btn echat-btn--ghost market-table__icon-btn"
                                      aria-label={`Добавить ${item.supplierName} в список`}
                                      title="Добавить в список"
                                      onClick={() => setListDialogItem(item)}
                                    >
                                      <IconListPlus />
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="echat-btn echat-btn--ghost market-table__icon-btn market-table__icon-btn--danger"
                                    aria-label={`Удалить ${item.supplierName}`}
                                    title="Удалить"
                                    disabled={deletingId === item.id || loading}
                                    onClick={() => setDeleteConfirmation({ kind: 'item', item })}
                                  >
                                    <IconTrash />
                                  </button>
                                  <button
                                    type="button"
                                    className="echat-btn echat-btn--ghost market-table__icon-btn"
                                    aria-label={
                                      isRestaurantMyPrices
                                        ? `Открыть карточку ${item.supplierName}`
                                        : expanded
                                          ? `Свернуть ${item.supplierName}`
                                          : `Изменить ${item.supplierName}`
                                    }
                                    title={isRestaurantMyPrices ? 'Карточка товара' : expanded ? 'Свернуть' : 'Изменить'}
                                    onClick={() =>
                                      isRestaurantMyPrices ? setCardItem(item) : setExpandedId(expanded ? null : item.id)
                                    }
                                  >
                                    <IconPencil />
                                  </button>
                                </div>
                              </td>
                            )
                            }
                          default:
                            return null
                        }
                      })}
                    </tr>
                    {expanded ? (
                      <tr className="market-table__edit-row">
                        <td colSpan={columnPrefs.visibleCount}>
                          <CatalogItemEditPanel
                            item={item}
                            organizationId={organizationId}
                            productTypes={productTypes}
                            brandOptions={brandOptions}
                            countryOptions={countryOptions}
                            catalogMode={catalogMode}
                            onSaved={handleItemSaved}
                            onDeleted={handleItemDeleted}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
        </DataTable>
        </>
      ) : null}

      {isRestaurantMyPrices && page && page.items.length > 0 ? (
        <PageScrollJumpButtons />
      ) : null}

      {cardItem ? (
        <ItemCardModal
          item={cardItem}
          organizationId={organizationId}
          productTypes={productTypes}
          brandOptions={brandOptions}
          countryOptions={countryOptions}
          onSaved={handleItemSaved}
          onDeleted={() => {
            setCardItem(null)
            handleItemDeleted()
          }}
          onDismiss={() => setCardItem(null)}
        />
      ) : null}

      {isRestaurantMyPrices && listDialogItem ? (
        <CatalogListDialog
          open
          itemName={listDialogItem.supplierName}
          lists={catalogLists}
          selectedListIds={listDialogItem.catalogListIds ?? []}
          busy={listUpdatingKey != null}
          onClose={() => setListDialogItem(null)}
          onToggle={(listId, selected) => void handleListToggle(listDialogItem, listId, selected)}
          onCreate={handleCreateList}
        />
      ) : null}

      {isRestaurantMyPrices ? (
        <CatalogListsManagerDialog
          open={listManagerOpen}
          lists={catalogLists}
          busy={listUpdatingKey != null}
          onClose={() => setListManagerOpen(false)}
          onCreate={handleCreateManagedList}
          onRename={handleRenameList}
          onOpenList={handleOpenList}
          onDelete={handleDeleteList}
        />
      ) : null}

      {isRestaurantMyPrices ? (
        <CatalogBulkListDialog
          open={bulkListDialogOpen}
          selectedCount={selectedIds.length}
          lists={catalogLists}
          busy={listUpdatingKey != null}
          onClose={() => setBulkListDialogOpen(false)}
          onAdd={handleBulkAddToList}
        />
      ) : null}

      <ConfirmDialog
        open={deleteConfirmation != null}
        title={deleteConfirmation?.kind === 'selection' ? 'Удалить выбранные позиции?' : 'Удалить позицию?'}
        message={
          deleteConfirmation?.kind === 'selection'
            ? `Будет удалено позиций: ${deleteConfirmation.itemIds.length}. Это действие нельзя отменить.`
            : deleteConfirmation?.kind === 'item'
              ? `Позиция «${deleteConfirmation.item.supplierName}» будет удалена без возможности восстановления.`
              : ''
        }
        confirmLabel="Удалить"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirmation(null)}
      />

      {!loading && page?.items.length === 0 ? (
        <MarketEmptyState
          title={myPricesEmptyState?.title ?? (isRestaurantMyPrices ? 'Прайсы не загружены' : 'Каталог пуст')}
          description={
            myPricesEmptyState?.description ?? (isRestaurantMyPrices
              ? 'Загрузите прайс поставщика в формате xlsx, xls или csv — позиции появятся здесь и в поиске каталога.'
              : 'Загрузите прайс-лист в формате xlsx, xls или csv — позиции появятся здесь после импорта.')
          }
          actions={
            myPricesEmptyState?.actions ?? (isRestaurantMyPrices
              ? [{ label: 'Импорт прайса', onClick: () => navigate(importPath) }]
              : [
                  { label: 'Импорт прайса', onClick: () => navigate(importPath) },
                  { label: 'Добавить позицию', onClick: () => setShowCreateForm(true), variant: 'ghost' },
                ])
          }
        />
      ) : null}

      {page && page.totalPages > 1 ? (
        <div className="market-pagination">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex <= 0}
            onClick={() => void load(pageIndex - 1, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)}
          >
            ←
          </button>
          <span>
            Страница {page.page + 1} из {page.totalPages}
          </span>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex >= page.totalPages - 1}
            onClick={() => void load(pageIndex + 1, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)}
          >
            →
          </button>
        </div>
      ) : null}
    </section>
  )
}
